import * as React from 'react';
import { lazy, Suspense } from 'react';
import './App.css';
import { createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Route, Routes, useLocation, useNavigationType } from 'react-router-dom';
import Home from './Components/Home/Home';
import ArticlePage from './Components/Article/ArticlePage';
import TerminalHeader from './Components/Header/TerminalHeader';
import ScrollProgress from './Components/Header/ScrollProgress';
import SkipLink from './Components/Header/SkipLink';
import AIChatbot from './Components/Chatbot/AIChatbot';
import { ThemeProvider, useTheme } from './theme/ThemeContext';
import { useThemeTokens } from './design/useThemeTokens';
import DotMatrixLoader from './design/DotMatrixLoader';
import ErrorPage from './Components/Error/Error';

const Resume = lazy(() => import('./Components/Resume/Resume'));
const Labs = lazy(() => import('./Components/Labs/Labs'));
const Playground = lazy(() => import('./Components/Playground/Playground'));
const Articles = lazy(() => import('./Components/Articles/Articles'));
const LearningRadar = lazy(() => import('./Components/Demos/LearningRadar').then(m => ({ default: m.LearningRadar })));
const FitCheck = lazy(() => import('./Components/Fit/FitCheck'));
const Cinema = lazy(() => import('./Components/Cinema/Cinema'));

// One table so the document title and the spoken announcement cannot drift.
const ROUTE_META = [
  { prefix: '/about', title: 'About — Abhijith Eanuga', label: 'About' },
  { prefix: '/articles', title: 'Writing — Abhijith Eanuga', label: 'Writing' },
  { prefix: '/article/', title: 'Article — Abhijith Eanuga', label: 'Article' },
  { prefix: '/labs', title: 'Labs — Abhijith Eanuga', label: 'Labs' },
  { prefix: '/playground', title: 'Playground — Abhijith Eanuga', label: 'Playground' },
  { prefix: '/fit', title: 'Fit Check — Abhijith Eanuga', label: 'Fit Check' },
  { prefix: '/cinema', title: 'Cinema — Abhijith Eanuga', label: 'Cinema' },
  { prefix: '/radar', title: 'Learning Radar — Abhijith Eanuga', label: 'Learning Radar' },
] as const;

const HOME_TITLE = 'Abhijith Eanuga — AI systems, from the inside out';

function routeMeta(pathname: string) {
  if (pathname === '/') return { title: HOME_TITLE, label: 'Home' };
  return ROUTE_META.find(entry => entry.prefix.endsWith('/')
    ? pathname.startsWith(entry.prefix)
    : pathname === entry.prefix || (entry.prefix === '/labs' && pathname.startsWith('/labs/')))
    ?? { title: 'Page not found — Abhijith Eanuga', label: 'Page not found' };
}

function PageLoading() {
  return (
    <main id="main" tabIndex={-1} className="bs-page bs-shell" aria-busy="true" style={{ minHeight: '100vh', paddingTop: 'clamp(48px, 8vw, 96px)' }}>
      <p className="bs-lede" role="status" style={{ display: 'inline-flex', alignItems: 'center', gap: 14 }}>
        <DotMatrixLoader size={24} />
        <span>Loading page…</span>
      </p>
    </main>
  );
}

// This effect commits with the destination, including slow lazy routes.
// Focusing in the app shell instead can target a removed loading screen.
function RouteReady({ onReady }: { onReady: (path: string) => void }) {
  const { pathname } = useLocation();
  React.useEffect(() => { onReady(pathname); }, [pathname, onReady]);
  return null;
}

// A stale tab can hold an index.html whose hashed chunk names no longer exist
// after a deploy. The dynamic import then 404s, which lands here and looks like
// a dead connection. Reload once to pick up the current build; the session guard
// stops a genuinely broken deploy from looping forever.
const CHUNK_RELOAD_KEY = 'page-boundary:reloaded';
const CHUNK_FAILURE = /loading chunk|dynamic import|importing a module script failed|failed to fetch dynamically imported module|error loading dynamically imported module|chunkloaderror/i;

function isChunkFailure(error: unknown): boolean {
  const message = error instanceof Error ? `${error.name}: ${error.message}` : String(error);
  return CHUNK_FAILURE.test(message);
}

export class PageBoundary extends React.Component<{ children: React.ReactNode; path: string }, { failed: boolean; stale: boolean }> {
  state = { failed: false, stale: false };

  static getDerivedStateFromError() { return { failed: true }; }

  componentDidCatch(error: unknown) {
    document.getElementById('main')?.focus({ preventScroll: true });
    if (!isChunkFailure(error)) return;
    let alreadyReloaded = false;
    try { alreadyReloaded = sessionStorage.getItem(CHUNK_RELOAD_KEY) === '1'; } catch { /* storage may be denied */ }
    if (alreadyReloaded) { this.setState({ stale: true }); return; }
    try { sessionStorage.setItem(CHUNK_RELOAD_KEY, '1'); } catch { /* storage may be denied */ }
    window.location.reload();
  }

  componentDidUpdate(previous: Readonly<{ children: React.ReactNode; path: string }>) {
    if (previous.path !== this.props.path && this.state.failed) this.setState({ failed: false, stale: false });
  }

  // A deliberate retry must be allowed to try the current build again.
  reload = () => {
    try { sessionStorage.removeItem(CHUNK_RELOAD_KEY); } catch { /* storage may be denied */ }
    window.location.reload();
  };

  render() {
    if (this.state.failed) {
      return (
        <main id="main" tabIndex={-1} className="bs-page bs-shell" style={{ minHeight: '70vh', paddingTop: 'clamp(48px, 8vw, 96px)' }}>
          <h1 className="bs-display">Page could not load</h1>
          <p className="bs-lede" style={{ margin: '24px 0 32px' }}>
            {this.state.stale
              ? 'This page did not load after reloading, so the last update may still be rolling out. Try again in a moment.'
              : 'Check your connection and try again.'}
          </p>
          <button className="bs-btn bs-btn--accent" onClick={this.reload}>Reload page</button>
        </main>
      );
    }
    return this.props.children;
  }
}

function AppInner() {
  const { resolvedMode, mode, setMode } = useTheme();
  const [accentFill, accentInk, ground, raised, ink, muted] = useThemeTokens(
    '--bs-accent-fill', '--bs-accent-ink', '--bs-ground', '--bs-raised', '--bs-ink', '--bs-muted',
  );
  const location = useLocation();
  const navigationType = useNavigationType();
  const previousPath = React.useRef(location.pathname);
  const readyPath = React.useRef(location.pathname);
  const [announcement, setAnnouncement] = React.useState('');

  const onRouteReady = React.useCallback((path: string) => {
    if (readyPath.current === path) return;
    readyPath.current = path;
    document.getElementById('main')?.focus({ preventScroll: true });
    setAnnouncement(`${routeMeta(path).label} loaded`);
  }, []);

  // New destinations start at their heading. Browser Back/Forward retains
  // native history restoration instead of being forced back to the top.
  // Either way focus moves to the new page's main landmark, so keyboard and
  // screen-reader users are not stranded on a link that no longer exists.
  React.useEffect(() => {
    const pathChanged = previousPath.current !== location.pathname;
    previousPath.current = location.pathname;
    if (!pathChanged) return;
    const isPop = navigationType === 'POP';
    if (!isPop) window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [location.pathname, navigationType]);

  React.useLayoutEffect(() => {
    document.title = routeMeta(location.pathname).title;
  }, [location.pathname]);

  const muiTheme = React.useMemo(
    () => createTheme({
      palette: {
        mode: resolvedMode,
        // MUI needs concrete colors. Its mode defaults cover the first render
        // until the shared CSS roles have been read.
        ...(accentFill && accentInk ? { primary: { main: accentFill, contrastText: accentInk } } : {}),
        ...(ground && raised ? { background: { default: ground, paper: raised } } : {}),
        ...(ink && muted ? { text: { primary: ink, secondary: muted } } : {}),
      },
      typography: { fontFamily: 'Inter, system-ui, sans-serif' },
      shape: { borderRadius: 12 },
      components: {
        MuiCssBaseline: {
          styleOverrides: { body: { backgroundColor: 'var(--bs-ground)', color: 'var(--bs-ink)' } },
        },
      },
    }),
    [resolvedMode, accentFill, accentInk, ground, raised, ink, muted],
  );

  return (
    <MuiThemeProvider theme={muiTheme}>
      <CssBaseline />
      <SkipLink />
      <p aria-live="polite" aria-atomic className="visually-hidden">{announcement}</p>
      <ScrollProgress />
      <TerminalHeader mode={mode} setMode={setMode} />
      <PageBoundary path={location.pathname}>
        <Suspense fallback={<PageLoading />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<Resume toggleTheme={() => setMode(resolvedMode === 'dark' ? 'light' : 'dark')} mode={resolvedMode} />} />
            <Route path="/articles" element={<Articles />} />
            <Route path="/article/:slug" element={<ArticlePage />} />
            <Route path="/labs" element={<Labs />} />
            <Route path="/labs/:labId" element={<Labs />} />
            <Route path="/playground" element={<Playground />} />
            <Route path="/fit" element={<FitCheck />} />
            <Route path="/cinema" element={<Cinema />} />
            <Route path="/radar" element={<LearningRadar />} />
            <Route path="*" element={<ErrorPage />} />
          </Routes>
          <RouteReady onReady={onRouteReady} />
        </Suspense>
      </PageBoundary>
      <AIChatbot mode={mode} setMode={setMode} />
    </MuiThemeProvider>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AppInner />
    </ThemeProvider>
  );
}

export default App;
